import { createAsyncThunk, createSlice, createSelector } from "@reduxjs/toolkit";
import {
  listSystemPrompts,
} from "../graphql/queries";
import {
  createSystemPrompt as createSystemPromptMutation,
  updateSystemPrompt as updateSystemPromptMutation,
  deleteSystemPrompt as deleteSystemPromptMutation,
} from "../graphql/mutations";
import { generateClient } from "aws-amplify/api";

const client = generateClient();

export interface SystemPrompt {
  id: string;
  promptText: string;
  description?: string;
  rating?: number;
  isDefault?: boolean;
  /** Surface this record serves (chat/schedule/…); null/absent = the legacy shared record every surface falls back to. */
  surface?: string | null;
  /** Model tier this surface runs, from the server catalog (GET /mcp/models); null = code default. */
  model?: string | null;
}

/** Records group by surface; null/''/whitespace all mean the same untagged legacy group. */
const normalizeSurface = (surface?: string | null): string => (surface ?? "").trim().toLowerCase();

/** Surface ids must match SURFACE_CHAT (mcpProxyFunction/src/systemPrompt.js) and SURFACE_SCHEDULE (agentScheduleExecutorFunction/src/index.js). */
export const SURFACE_SHARED = "";
export const SURFACE_CHAT = "chat";
export const SURFACE_SCHEDULE = "schedule";

export const SYSTEM_PROMPT_SURFACES: { id: string; name: string; hint: string }[] = [
  { id: SURFACE_SHARED, name: "Shared (all surfaces)", hint: "The legacy record every surface falls back to when it has no record of its own." },
  { id: SURFACE_CHAT, name: "AI Analyst chat", hint: "Interactive chat only. Overrides the shared record." },
  { id: SURFACE_SCHEDULE, name: "Scheduled runs", hint: "Scheduled answers emailed to customers. Overrides the shared record." },
];

interface SystemPromptState {
  loading: "idle" | "pending" | "success" | "failed";
  error: string | null;
  systemPrompts: Record<string, SystemPrompt>;
}

const initialState: SystemPromptState = {
  loading: "idle",
  error: null,
  systemPrompts: {},
};

export const fetchSystemPrompts = createAsyncThunk(
  "systemPrompts/fetchAll",
  async (_, thunkAPI) => {
    try {
      const result = (await client.graphql({ query: listSystemPrompts })) as any;
      const dict = (result.data.listSystemPrompts?.items ?? []).reduce(
        (acc: Record<string, SystemPrompt>, curr: SystemPrompt) => ({ ...acc, [curr.id]: curr }),
        {} as Record<string, SystemPrompt>
      );
      return dict;
    } catch (error: any) {
      console.error("fetch system prompts error", error);
      return thunkAPI.rejectWithValue(error?.message ?? "Failed to fetch");
    }
  }
);

export const createSystemPrompt = createAsyncThunk(
  "systemPrompts/create",
  async (
    input: {
      id?: string;
      promptText: string;
      description?: string;
      rating?: number;
      isDefault?: boolean;
      surface?: string | null;
      model?: string | null;
    },
    thunkAPI
  ) => {
    try {
      const result = (await client.graphql({
        query: createSystemPromptMutation,
        variables: { input },
      })) as any;
      return result.data.createSystemPrompt;
    } catch (error: any) {
      console.error("create system prompt error", error);
      return thunkAPI.rejectWithValue(error?.message ?? "Failed to create");
    }
  }
);

export const updateSystemPrompt = createAsyncThunk(
  "systemPrompts/update",
  async (
    input: {
      id: string;
      promptText?: string;
      description?: string;
      rating?: number;
      isDefault?: boolean;
      surface?: string | null;
      model?: string | null;
    },
    thunkAPI
  ) => {
    try {
      const result = (await client.graphql({
        query: updateSystemPromptMutation,
        variables: { input },
      })) as any;
      return result.data.updateSystemPrompt;
    } catch (error: any) {
      console.error("update system prompt error", error);
      return thunkAPI.rejectWithValue(error?.message ?? "Failed to update");
    }
  }
);

export const deleteSystemPromptFunc = createAsyncThunk(
  "systemPrompts/delete",
  async (input: { id: string }, thunkAPI) => {
    try {
      const result = (await client.graphql({
        query: deleteSystemPromptMutation,
        variables: { input },
      })) as any;
      return result.data.deleteSystemPrompt;
    } catch (error: any) {
      console.error("delete system prompt error", error);
      return thunkAPI.rejectWithValue(error?.message ?? "Failed to delete");
    }
  }
);

/** Set one prompt as default for ITS surface; only records on that same surface are set to non-default. */
export const setDefaultSystemPrompt = createAsyncThunk(
  "systemPrompts/setDefault",
  async (id: string, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as { systemPrompts: SystemPromptState };
      const prompts = Object.values(state.systemPrompts.systemPrompts ?? {});
      const target = prompts.find((p) => p.id === id);
      if (!target) {
        return thunkAPI.rejectWithValue("Prompt not found");
      }
      // Each surface owns its own default, so clearing must stay inside the target's surface — a global rewrite would strip the default off every OTHER surface (and off the legacy untagged record, silently dropping both lambdas to their code fallback).
      // Only records that actually change are written, target first: a mid-loop failure then
      // leaves the surface with a default present (possibly two, self-healing on retry) rather
      // than none — no default silently drops the surface to the shared record/code fallback.
      const targetSurface = normalizeSurface(target.surface);
      const changed = prompts
        .filter((p) => normalizeSurface(p.surface) === targetSurface && !!p.isDefault !== (p.id === id))
        .sort((a, b) => Number(b.id === id) - Number(a.id === id));
      for (const p of changed) {
        await client.graphql({
          query: updateSystemPromptMutation,
          variables: {
            input: { id: p.id, isDefault: p.id === id },
          },
        });
      }
      const result = (await client.graphql({ query: listSystemPrompts })) as any;
      const dict = (result.data.listSystemPrompts?.items ?? []).reduce(
        (acc: Record<string, SystemPrompt>, curr: SystemPrompt) => ({ ...acc, [curr.id]: curr }),
        {} as Record<string, SystemPrompt>
      );
      return dict;
    } catch (error: any) {
      console.error("set default system prompt error", error);
      return thunkAPI.rejectWithValue(error?.message ?? "Failed to set default");
    }
  }
);

const systemPromptSlice = createSlice({
  name: "systemPrompts",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSystemPrompts.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(fetchSystemPrompts.fulfilled, (state, action) => {
        state.loading = "success";
        state.systemPrompts = action.payload;
      })
      .addCase(fetchSystemPrompts.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.payload as string | null;
      })
      .addCase(createSystemPrompt.fulfilled, (state, action) => {
        if (action.payload?.id) state.systemPrompts[action.payload.id] = action.payload;
      })
      .addCase(updateSystemPrompt.fulfilled, (state, action) => {
        if (action.payload?.id) state.systemPrompts[action.payload.id] = action.payload;
      })
      .addCase(deleteSystemPromptFunc.fulfilled, (state, action) => {
        if (action.payload?.id) delete state.systemPrompts[action.payload.id];
      })
      .addCase(setDefaultSystemPrompt.fulfilled, (state, action) => {
        state.systemPrompts = action.payload ?? state.systemPrompts;
      });
  },
});

/** Every surface's default record plus the shared one, indexed once so lookups need no selector arg. */
const selectDefaultsBySurface = createSelector(
  (state: { systemPrompts: SystemPromptState }) => state.systemPrompts.systemPrompts,
  (prompts) => {
    const defaults = Object.values(prompts).filter((p) => p.isDefault);
    const bySurface: Record<string, SystemPrompt> = {};
    for (const p of defaults) bySurface[normalizeSurface(p.surface)] = p;
    return { bySurface, shared: bySurface[SURFACE_SHARED] ?? null };
  }
);

export const selectDefaultForSurface = (
  state: { systemPrompts: SystemPromptState },
  surface?: string | null
): SystemPrompt | null => {
  const { bySurface, shared } = selectDefaultsBySurface(state);
  return bySurface[normalizeSurface(surface)] ?? shared;
};

export interface SystemPromptGroup {
  surface: string;
  name: string;
  hint: string;
  prompts: SystemPrompt[];
}

/** Known surfaces first (in declared order), then any surface only present in the data; empty groups are dropped. */
export const selectPromptsGroupedBySurface = createSelector(
  (state: { systemPrompts: SystemPromptState }) => state.systemPrompts.systemPrompts,
  (prompts): SystemPromptGroup[] => {
    const records = Object.values(prompts);
    const known = SYSTEM_PROMPT_SURFACES.map((s) => s.id);
    const extra = Array.from(new Set(records.map((p) => normalizeSurface(p.surface))))
      .filter((s) => !known.includes(s))
      .sort();
    return [...SYSTEM_PROMPT_SURFACES, ...extra.map((id) => ({ id, name: id, hint: "Surface not known to the app — no lambda reads it." }))]
      .map((s) => ({
        surface: s.id,
        name: s.name,
        hint: s.hint,
        // Default first so the active record for each surface is always the top line.
        prompts: records
          .filter((p) => normalizeSurface(p.surface) === s.id)
          .sort((a, b) => Number(!!b.isDefault) - Number(!!a.isDefault)),
      }))
      .filter((g) => g.prompts.length > 0);
  }
);

export default systemPromptSlice.reducer;
